import { useCallback } from 'react';

import { getLengthOfLongestArray } from '~/utils/helpers/array';

export type PairedDataItem<TDataItem> = {
  oldData: TDataItem;
  newData: TDataItem;
};

export type DataPairingProcessPayload<TDataItem> = {
  unprocessed: {
    oldArray: TDataItem[];
    newArray: TDataItem[];
  };
  paired: PairedDataItem<TDataItem>[];
  checkList: {
    [p: string]: boolean;
  };
};

export const useDataPairingForComparison = () => {
  const hasCompletedDataItemPairProcess = useCallback(
    <TDataItem>(payload: DataPairingProcessPayload<TDataItem>): boolean => {
      return (
        !payload.unprocessed.oldArray.length &&
        !payload.unprocessed.newArray.length
      );
    },
    [],
  );

  const separateDataItemFromArray = useCallback(
    <TDataItem>(
      dataArray: TDataItem[],
      index: number,
    ): {
      array: TDataItem[];
      item?: TDataItem;
    } => {
      if (index < 0 || dataArray.length < 1) {
        return {
          array: dataArray,
          item: undefined,
        };
      }

      const array = [...dataArray];
      const item = array.splice(index, 1)[0];

      return {
        array,
        item,
      };
    },
    [],
  );

  const getUpdatedPayload = useCallback(
    <TDataItem>(
      previousPayload: DataPairingProcessPayload<TDataItem>,
      updatedOld: {
        array: TDataItem[];
        item?: TDataItem;
      },
      updatedNew: {
        array: TDataItem[];
        item?: TDataItem;
      },
      emptyItem: TDataItem,
    ): DataPairingProcessPayload<TDataItem> => {
      const { paired, checkList } = previousPayload;

      return {
        unprocessed: {
          oldArray: updatedOld.array,
          newArray: updatedNew.array,
        },
        paired: [
          ...paired,
          {
            oldData: updatedOld.item ?? emptyItem,
            newData: updatedNew.item ?? emptyItem,
          },
        ],
        checkList,
      };
    },
    [],
  );

  const resolveDataItemPair = useCallback(
    <TDataItem>(
      previousPayload: DataPairingProcessPayload<TDataItem>,
      indexResolver: (dataArray: TDataItem[]) => number,
      emptyItem: TDataItem,
    ): {
      payload: DataPairingProcessPayload<TDataItem>;
      pairFound: boolean;
    } => {
      const {
        unprocessed: { oldArray, newArray },
      } = previousPayload;

      const dataOld = separateDataItemFromArray(
        oldArray,
        indexResolver(oldArray),
      );
      const dataNew = separateDataItemFromArray(
        newArray,
        indexResolver(newArray),
      );

      // No pair found
      if (!dataOld.item || !dataNew.item) {
        return {
          payload: previousPayload,
          pairFound: false,
        };
      }
      return {
        payload: getUpdatedPayload<TDataItem>(
          previousPayload,
          dataOld,
          dataNew,
          emptyItem,
        ),
        pairFound: true,
      };
    },
    [separateDataItemFromArray, getUpdatedPayload],
  );

  const resolveRestDataItemPairs = useCallback(
    <TDataItem>(
      previousPayload: DataPairingProcessPayload<TDataItem>,
      emptyItem: TDataItem,
    ) => {
      const itemCount = getLengthOfLongestArray(
        previousPayload.unprocessed.oldArray,
        previousPayload.unprocessed.newArray,
      );

      return Array.from(Array(itemCount).keys()).reduce(
        (lastPayload: DataPairingProcessPayload<TDataItem>) => {
          const {
            unprocessed: { oldArray, newArray },
          } = lastPayload;

          const separateNextInArray = (array: TDataItem[]) =>
            array.length
              ? separateDataItemFromArray(array, 0)
              : { array: [], item: emptyItem };

          return getUpdatedPayload(
            lastPayload,
            separateNextInArray(oldArray),
            separateNextInArray(newArray),
            emptyItem,
          );
        },
        previousPayload,
      );
    },
    [separateDataItemFromArray, getUpdatedPayload],
  );

  return {
    separateDataItemFromArray,
    resolveDataItemPair,
    resolveRestDataItemPairs,
    hasCompletedDataItemPairProcess,
  };
};
